import { useParams } from "react-router-dom";
import { TitlePage } from "../../components/ui/TitlePage";
import { useEffect, useState, useCallback } from "react";
import { api, urls } from "../../services/api";
import { SubTitlePage } from "../../components/ui/SubTitlePage";
import { ItemCategory } from "../../components/ui/ItemCategory";
import FlatList from "flatlist-react/lib";
import { IMenuItem, IMenu } from "../../types/menu";
import { useApp } from "../../context/App";
import { Banner } from "../../components/sections/Banner";
import { IEntity } from "../../types/entity";
import { NewsShare } from "../../components/sections/NewsShare";
import DOMPurify from 'dompurify';
import { motion } from "framer-motion";

export function EntityScreen() {

	let { id } = useParams();
	const { mobile, configApiURL, getTenantUrl } = useApp()
	const [entity, setEntity] = useState<IEntity>()
	const [listMenu, setMenuList] = useState<IMenu[]>([])
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [id])

	const getEntityById = useCallback(async (entityId: number) => {
		try {
			const response = await api.get(urls.Entity + entityId)
			setEntity(response.data)
		} catch (error) {
			// silent
		} finally {
			setIsLoading(false)
		}
	}, []);

	useEffect(() => {
		if (id) {
			setIsLoading(true)
			// Optional: Clear previous entity to avoid flickering old data
			// setEntity(undefined) 
			getEntityById(parseInt(id));
		}
	}, [id, getEntityById]);

	useEffect(() => {
		(async () => {
			try {
				const response = await api.get(urls.menus)
				const menu: IMenuItem[] = response.data
				let menuFiltered: IMenu[] = []
				let idSection = 0
				if (menu && menu.length > 0 && menu[0].secoes) {
					menu[0].secoes.forEach(itemSecoes => {
						itemSecoes.menus?.forEach(itemMenu => {
							if (parseInt(id || '0') === itemMenu.ref_cod_entidade) {
								idSection = itemMenu.ref_cod_menu_secao || 0
							}
						})
						if (itemSecoes.cod_menu_secao === idSection) {
							menuFiltered = itemSecoes.menus as IMenu[]
						}
					})
				}
				setMenuList([...menuFiltered])
			} catch (error) {
				// silent
			}
		})()
	}, [id])


	const apiUrl = configApiURL.replace("portaladm-pmitajai/api/", "public/")

	// Transform sequence:
	// 1. Fix images src="/... to point to full API public path
	// 2. Fix links pointing to itajai.sc.gov.br to be relative paths
	let content = (entity?.descricao || '').replaceAll('src="/', `src="${apiUrl}`)
	content = content.replaceAll('https://itajai.sc.gov.br/e/', '/e/')
	content = content.replaceAll('http://itajai.sc.gov.br/e/', '/e/')
	content = content.replaceAll('https://itajai.sc.gov.br/a/', '/a/')
	content = content.replaceAll('http://itajai.sc.gov.br/a/', '/a/')
	content = content.replaceAll('https://itajai.sc.gov.br/', '/')
	content = content.replaceAll('http://itajai.sc.gov.br/', '/')

	const pageVariants = {
		initial: { opacity: 0, y: 10 },
		animate: { opacity: 1, y: 0 },
		exit: { opacity: 0, y: -10 }
	};

	return (
		<div className="flex flex-col w-full min-h-screen bg-white dark:bg-surface-dark transition-colors items-center justify-start font-roboto">
			{mobile && <div className="h-20" />}

			<TitlePage title={entity?.nome ? entity?.nome : (isLoading ? 'Carregando...' : 'Sem Nome')} />

			<motion.div
				key={id}
				initial="initial"
				animate="animate"
				variants={pageVariants}
				transition={{ duration: 0.4 }}
				className="flex flex-col lg:flex-row justify-between w-11/12 lg:w-3/5 mt-8 mb-16 lg:mt-10 lg:mb-20 gap-10"
			>
				{!mobile && (
					<div className="flex flex-col w-[30%] hidden lg:flex">
						{listMenu.length > 0 && (
							<FlatList
								list={listMenu}
								renderItem={(item) => (
									<ItemCategory
										key={item.cod_menu}
										title={item.titulo || ''}
										isActive={item.ref_cod_entidade === parseInt(id || '0')}
										href={getTenantUrl(item.ref_cod_artigo ? `/a/${item.ref_cod_artigo}` : (item.ref_cod_entidade ? `/e/${item.ref_cod_entidade}` : (item.link && item.link !== '#' ? item.link : '#')))}
										className="text-sm mb-1 uppercase"
									/>
								)}
							/>
						)}
					</div>
				)}

				<div className="flex flex-col w-full lg:w-[70%] text-gray-800 dark:text-gray-200">
					<div
						className="w-full font-light text-base lg:text-base leading-relaxed break-words dark-prose mb-8"
						dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content || '') }}
					/>

					<div className="flex flex-row justify-start w-full bg-transparent font-light text-base mt-2 mb-2">
						{entity?.nome_gestor && (
							<div className="flex flex-col w-full">
								<SubTitlePage title={entity?.titulo_gestor} />
								<div className="w-full text-base mt-2">{entity?.nome_gestor}</div>
							</div>
						)}
					</div>

					<div className="flex flex-row justify-start w-full bg-transparent font-light text-base mt-4 mb-4">
						{entity?.horario_atendimento && (
							<div className="flex flex-col w-full">
								<SubTitlePage title="Horário de Atendimento" />
								<div className="w-full text-base mt-2">{entity?.horario_atendimento}</div>
							</div>
						)}
					</div>

					<div className="flex flex-col lg:flex-row justify-between w-full font-light text-base gap-6 lg:gap-8 mt-4 mb-4">
						{entity?.email && (
							<div className="flex flex-col w-full lg:w-1/2">
								<SubTitlePage title="Email" />
								<div className="w-full text-base mt-2 break-words">{entity?.email}</div>
							</div>
						)}
						{entity?.telefones && (
							<div className="flex flex-col w-full lg:w-1/2">
								<SubTitlePage title="Telefone" />
								<div className="w-full text-base mt-2 break-words">{entity?.telefones}</div>
							</div>
						)}
					</div>

					<div className="flex flex-row justify-start w-full mt-4 mb-10 font-light text-base">
						{entity?.endereco && (
							<div className="flex flex-col w-full">
								<SubTitlePage title="Endereço" />
								<div className="w-full text-base mt-2 break-words text-gray-700 dark:text-gray-300">
									{entity?.endereco} {entity?.numero && `,${entity?.numero}`}
									{entity?.bairro && <><br />{entity?.bairro}</>}
									{entity?.cidade && <><br />{entity?.cidade} (SC)</>}
								</div>
							</div>
						)}
					</div>

					<NewsShare />
				</div>
			</motion.div>

			<Banner mobile={mobile} />
		</div>
	);
}